import type { DistanceUnit } from "@/models/siteUnit.types";
import type {
	ICompactionOutline,
	TOffsetType,
} from "@/store/modules/spreading-compaction-types";
import { buildCompactionCsvString } from "./csvExporter";
import { buildLandXmlString } from "./xmlExporter";

interface IGeometryLike {
	vertices: number[];
	indices: number[];
}

interface IGeometryPart {
	type: TOffsetType;
	offset: number;
	geometry: IGeometryLike;
}

export interface SurfaceExportFile {
	fileName: string;
	content: string;
}

export function sanitizeSurfaceExportName(name: string): string {
	const sanitized = name
		.replace(/[\\/:*?"<>|]/g, "_")
		.split("")
		.map((character) => (character.charCodeAt(0) < 32 ? "_" : character))
		.join("")
		.trim();
	return sanitized || "spreading_compaction";
}

const MIN_LAYER_DIGITS = 2;

function layerDigits(count: number): number {
	return Math.max(MIN_LAYER_DIGITS, String(Math.max(1, count)).length);
}

function layerName(name: string, index: number, count: number): string {
	return `${sanitizeSurfaceExportName(name)}_layer${String(index + 1).padStart(layerDigits(count), "0")}`;
}

/**
 * レイヤ番号順 (CONTEXT.md「レイヤ」): オフセット降順 = 最下層が 1。
 * 表示・API 順はオフセット昇順のままなので、番号付け直前でのみ並べ替える。
 */
export function sortByLayerOrder<T extends { offset: number }>(
	parts: ReadonlyArray<T>,
): T[] {
	return [...parts].sort((a, b) => b.offset - a.offset);
}

/**
 * 全転圧オフセットに外周列が揃っているか。旧レコード (outline 無し) は false。
 * 層抜けの CSV は誤解を生むので、1 層でも欠ければ CSV 全体を出さない。
 */
export function hasCompactionOutlineForOffsets(
	compactionOffsets: ReadonlyArray<number>,
	outlines: ReadonlyArray<ICompactionOutline>,
): boolean {
	return compactionOffsets.every((offset) =>
		outlines.some((o) => o.offset === offset && o.points.length > 0),
	);
}

/**
 * Builds the canonical XML/CSV bytes shared by ZIP and Groupware exports.
 * 転圧 CSV は `compactionOutline` から作る。転圧層があるのに外周列が欠けた層が
 * あれば CSV は省略する (`hasCompactionOutlineForOffsets`)。
 */
export function buildSurfaceExportFiles(
	displayName: string,
	parts: ReadonlyArray<IGeometryPart>,
	distanceUnit: DistanceUnit,
	compactionOutline: ReadonlyArray<ICompactionOutline> = [],
): SurfaceExportFile[] {
	const spreading = sortByLayerOrder(
		parts.filter((part) => part.type === "spreading"),
	);
	const compactionOffsets = [
		...new Set(
			parts
				.filter((part) => part.type === "compaction")
				.map((part) => part.offset),
		),
	];
	const files: SurfaceExportFile[] = [];

	for (let i = 0; i < spreading.length; i++) {
		const projectName = layerName(displayName, i, spreading.length);
		files.push({
			fileName: `${projectName}.xml`,
			content: buildLandXmlString(
				spreading[i].geometry,
				projectName,
				undefined,
				distanceUnit,
			),
		});
	}

	if (
		compactionOffsets.length > 0 &&
		hasCompactionOutlineForOffsets(compactionOffsets, compactionOutline)
	) {
		const layers = sortByLayerOrder(
			compactionOutline.filter((o) => compactionOffsets.includes(o.offset)),
		);
		files.push({
			fileName: `${sanitizeSurfaceExportName(displayName)}_compaction.csv`,
			content: buildCompactionCsvString(
				layers.map((o) => ({ outline: o.points })),
				distanceUnit,
			),
		});
	}

	return files;
}
